/**
 * NewChatModal - Modal to select a friend and start a new chat
 */

import React, { useState, useEffect, useRef } from 'react';
import { useFriendsBasic } from '../../friends/hooks/useFriendsBasic';

interface NewChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFriend: (friendUserId: string) => Promise<void>;
}

export const NewChatModal: React.FC<NewChatModalProps> = ({
  isOpen,
  onClose,
  onSelectFriend,
}) => {
  // Use lightweight friends hook (no portfolio calculations - much faster)
  // Only fetch when modal is open (lazy loading)
  const { friends, loading } = useFriendsBasic(isOpen);
  const [searchQuery, setSearchQuery] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setIsStarting(false);
    }
  }, [isOpen]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        if (!isStarting) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isStarting, onClose]);

  if (!isOpen) return null;

  const handleSelectFriend = async (friendUserId: string) => {
    try {
      setIsStarting(true);
      await onSelectFriend(friendUserId);
      onClose();
    } catch (err) {
      console.error('Failed to start chat:', err);
      // Error handling could be improved with toast notifications
    } finally {
      setIsStarting(false);
    }
  };

  // Filter friends by search query
  const filteredFriends = friends.filter((friend) =>
    friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (friend.username && friend.username.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Map FriendSummary to the format we need
  const friendsForChat = filteredFriends.map((friend) => ({
    userId: friend.friendId,
    name: friend.name,
    username: friend.username,
    avatar: friend.profilePictureUrl,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Chat</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isStarting}
          >
            <svg
              className="w-5 h-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search friends..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isStarting}
          />
        </div>

        {/* Friends list */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <svg
                className="animate-spin h-8 w-8 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : friendsForChat.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              {searchQuery ? 'No friends found' : 'No friends yet'}
            </div>
          ) : (
            friendsForChat.map((friend) => (
              <button
                key={friend.userId}
                onClick={() => handleSelectFriend(friend.userId)}
                disabled={isStarting}
                className="w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* Avatar */}
                {friend.avatar ? (
                  <img
                    src={friend.avatar}
                    alt={friend.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-sm text-gray-600">
                      {friend.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Name and username */}
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-gray-900">{friend.name}</p>
                  {friend.username && (
                    <p className="text-xs text-gray-500">@{friend.username}</p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

